// Dashboard set as the kit's Whoop/Strava-style scoreboard. Top to bottom:
//
//   1. TopBar      — crumb + live "X readings this week" pulse
//   2. HeroStrip   — 4 ring tiles: reading score / this week / top dim / watch
//   3. row-2       — FocusTile (auto-detected focus) + UpcomingTile (stub V1)
//   4. row-3       — RecentReadingsList + DimensionsPanel
//
// Streak intentionally absent. Meetings don't fall on a daily cadence and
// people don't work weekends; counting consecutive weeks is the wrong
// incentive for a coaching surface.
//
// Replaces the prior editorial "weekly issue" layout. Editorial components
// (Masthead, CoverNumber, Wall*, PullQuote, Colophon) stay on disk but are
// no longer imported here -- kept around for a possible future weekly digest.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  getMe,
  getPatterns,
  getTrends,
  getConnectorStatus,
  listAnalyses,
  SessionExpiredError,
  type AnalysisListItem,
  type ConnectorsStatusResponse,
  type Dimension,
  type Me,
  type PatternsResponse,
  type TrendsResponse,
  type TrendDimension,
} from "../lib/api";
import { TopBar } from "../components/TopBar";
import { HeroStrip } from "../components/HeroStrip";
import { FocusTile } from "../components/FocusTile";
import { UpcomingTile } from "../components/UpcomingTile";
import { RecentReadingsList } from "../components/RecentReadingsList";
import { DimensionsPanel } from "../components/DimensionsPanel";

const DIMENSION_LABEL: Record<Dimension, string> = {
  clarity: "Clarity",
  confidence: "Confidence",
  conciseness: "Conciseness",
  listening: "Listening",
  structure: "Structure",
  pacing: "Pacing",
};

const RECENT_LIMIT = 5;
const LIST_PROBE_LIMIT = 60;

type LoadState =
  | { kind: "loading" }
  | {
      kind: "ok";
      me: Me;
      readings: AnalysisListItem[];
      trends30: TrendsResponse;
      trends7: TrendsResponse;
      patterns: PatternsResponse;
      connectors: ConnectorsStatusResponse;
    }
  | { kind: "error"; message: string };

export function Dashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const loadAll = useCallback(async () => {
    try {
      const [me, list, trends30, trends7, patterns, connectors] = await Promise.all([
        getMe(),
        listAnalyses({ limit: LIST_PROBE_LIMIT }),
        getTrends({ window: "30d" }),
        getTrends({ window: "7d" }),
        getPatterns({ days: 30 }),
        getConnectorStatus(),
      ]);
      setState({ kind: "ok", me, readings: list.items, trends30, trends7, patterns, connectors });
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        navigate("/login", { replace: true });
        return;
      }
      setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }, [navigate]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  if (state.kind === "loading") return <DashboardSkeleton />;
  if (state.kind === "error") return <DashboardError message={state.message} />;

  return <DashboardLoaded {...state} />;
}

function DashboardLoaded({
  me,
  readings,
  trends30,
  trends7,
  patterns,
  connectors,
}: Extract<LoadState, { kind: "ok" }>) {
  const totalReadings = readings.length; // last 60; close enough for "of N" text on the recent panel
  const recentReadings = readings.slice(0, RECENT_LIMIT);

  const topDim = pickTopDim(trends30.dimensions);
  const watchDim = pickWatchDim(trends30.dimensions);

  const ringScore = trends30.overall.average ?? 0;
  const ringDelta = trends30.overall.deltaVsPrior ?? null;

  const focusProps = pickAutoFocus(me, patterns);
  const upcomingConnected = connectors.granola.connected || connectors.fathom.connected;

  const readingsThisWeek = trends7.readingsCount;

  // 0-readings empty state. Strip collapses, focus prompts; recent + dims hide.
  if (readings.length === 0) {
    return (
      <>
        <TopBar label="This week" detail={formatToday()} live={{ count: 0 }} />
        <EmptyDashboard hasFocus={(me.practiceFocus?.length ?? 0) > 0} />
      </>
    );
  }

  return (
    <>
      <TopBar label="This week" detail={formatToday()} live={{ count: readingsThisWeek }} />

      <HeroStrip
        ringScore={ringScore}
        ringDelta={ringDelta}
        thisWeek={{
          avg: trends7.overall.average ?? 0,
          deltaVsPrior: trends7.overall.deltaVsPrior,
        }}
        topDim={
          topDim
            ? { score: topDim.average ?? 0, name: DIMENSION_LABEL[topDim.name], deltaVsPrior: topDim.deltaVsPrior }
            : { score: 0, name: "—", deltaVsPrior: null }
        }
        watchDim={
          watchDim
            ? {
                score: watchDim.average ?? 0,
                name: DIMENSION_LABEL[watchDim.name],
                deltaVsPrior: watchDim.deltaVsPrior,
              }
            : { score: 0, name: "—", deltaVsPrior: null }
        }
      />

      <section className="row-2">
        <FocusTile {...focusProps} />
        <UpcomingTile kind="stub" connected={upcomingConnected} />
      </section>

      <section className="row-3">
        <RecentReadingsList items={recentReadings} total={totalReadings} />
        <DimensionsPanel dimensions={trends30.dimensions} />
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Empty + loading + error states.

function EmptyDashboard({ hasFocus }: { hasFocus: boolean }) {
  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 38, letterSpacing: "-0.035em", margin: "0 0 12px" }}>
        Quietly clearer.
      </h1>
      <p className="lede" style={{ marginBottom: 22 }}>
        Connect Granola or Fathom and Klemento reads each meeting as it lands. Each reading earns a place on your trend line, and your practice focus is checked on every one.
      </p>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Link to="/settings" className="btn btn-primary">Connect a notetaker</Link>
        {!hasFocus ? (
          <Link to="/settings" className="btn btn-secondary">Set practice focus</Link>
        ) : null}
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <>
      <TopBar label="This week" detail="Loading…" />
      <section className="strip" aria-hidden="true" style={{ opacity: 0.5 }}>
        {["Reading score", "This week", "Top dimension", "Watch"].map((label) => (
          <div key={label} className="ring-tile">
            <span className="l">{label}</span>
            <div className="score-ring" style={{ width: 110, height: 110 }}>
              <svg width={110} height={110} viewBox="0 0 110 110">
                <circle className="ring-bg" cx={55} cy={55} r={51} fill="none" strokeWidth={8} />
              </svg>
              <div className="ring-centre">
                <span className="ring-score">··</span>
              </div>
            </div>
            <div className="ring-foot">
              <span className="delta flat">·</span>
            </div>
          </div>
        ))}
      </section>
    </>
  );
}

function DashboardError({ message }: { message: string }) {
  return (
    <div style={{ maxWidth: 600 }}>
      <p className="frame-label" style={{ marginBottom: 12 }}>Could not load dashboard</p>
      <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, letterSpacing: "-0.03em", margin: "0 0 12px" }}>
        Something stalled.
      </h1>
      <p style={{ color: "rgb(var(--color-ink-muted))", marginBottom: 18 }}>{message}</p>
      <Link to="/settings" className="btn btn-secondary">Open settings</Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Derivations. Dimension picks + focus auto-detect.

function pickTopDim(dims: TrendDimension[]): TrendDimension | null {
  const ranked = dims
    .filter((d) => typeof d.average === "number")
    .slice()
    .sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
  return ranked[0] ?? null;
}

function pickWatchDim(dims: TrendDimension[]): TrendDimension | null {
  const eligible = dims.filter((d) => typeof d.average === "number");
  if (eligible.length === 0) return null;
  // Prefer the dimension that's trending down most. Fallback: lowest avg.
  const ranked = eligible.slice().sort((a, b) => {
    const da = a.deltaVsPrior ?? 0;
    const db = b.deltaVsPrior ?? 0;
    if (da !== db) return da - db;
    return (a.average ?? 0) - (b.average ?? 0);
  });
  return ranked[0] ?? null;
}

// Auto-detect focus. Pick the most recently slipping focus arc; fall back to
// a CTA if no focus exists OR no data supports any.

function pickAutoFocus(me: Me, patterns: PatternsResponse): React.ComponentProps<typeof FocusTile> {
  const focusItems = me.practiceFocus ?? [];
  const arcs = patterns.focusArcs ?? [];

  if (focusItems.length === 0) {
    return {
      kind: "cta",
      headline: "Pick one or two behaviours you're working on.",
      body: "Klemento checks them on every reading. The best users pick one and stick with it for a few weeks.",
      ctaLabel: "Set your practice focus",
      ctaTo: "/settings",
    };
  }

  // Prefer an arc that's slipping in the last 14 days with at least 3 readings.
  const fourteenDaysAgo = Math.floor(Date.now() / 1000) - 14 * 86400;
  const slipping = arcs
    .filter((arc) => arc.timeline.length >= 3)
    .map((arc) => {
      const recent = arc.timeline.filter((t) => t.createdAt >= fourteenDaysAgo);
      const slipped = recent.filter((t) => t.status === "slipped" || t.status === "absent").length;
      return { arc, slipped, recentCount: recent.length };
    })
    .filter((entry) => entry.slipped >= 1)
    .sort((a, b) => b.slipped - a.slipped);

  const lead = slipping[0]?.arc ?? arcs[0];

  if (!lead) {
    // Focus item exists but no arc data yet. Encourage connecting a notetaker
    // so meetings start syncing in.
    const first = focusItems[0]!;
    return {
      kind: "active",
      headline: first.label,
      body: "Klemento will start tracking how this shows up as soon as the next meeting syncs in.",
      ctaLabel: "Connect a notetaker",
      ctaTo: "/settings",
      altLabel: "Edit focus",
      altTo: "/settings",
    };
  }

  return {
    kind: "active",
    headline: lead.label,
    body: focusBody(lead.trendLabel),
    ctaLabel: "View detail",
    ctaTo: "/reports",
    altLabel: "Pick a different focus",
    altTo: "/settings",
  };
}

function focusBody(trend: "improving" | "slipping" | "stable" | "absent"): string {
  switch (trend) {
    case "slipping":
      return "Last few readings showed this slipping. Lead with it on your next call.";
    case "improving":
      return "It's showing up more often. Keep the cadence; small wins compound.";
    case "stable":
      return "Holding steady across recent readings. Push for one specific upgrade this week.";
    case "absent":
    default:
      return "Not enough recent evidence yet. Bring another reading and check back.";
  }
}

// ---------------------------------------------------------------------------

function formatToday(): string {
  return new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
